import { useEffect, useState } from 'react'
import { Package, X } from 'lucide-react'
import { supabase, isDemoMode } from '../lib/supabase'

// Read-only product catalogue for client users. Shows everything that's been
// made visible to the active site (its card_products), as cards in a grid.
// Click a card → full detail in a modal.
function effective(cp, field) {
  if (cp.overrides && Object.prototype.hasOwnProperty.call(cp.overrides, field)) {
    return cp.overrides[field]
  }
  return cp.products?.[field]
}

export default function Catalogue({ activeCompanyId }) {
  const [products, setProducts] = useState([])
  const [loading, setLoading] = useState(false)
  const [open, setOpen] = useState(null) // selected card_product for modal

  useEffect(() => {
    if (!activeCompanyId || isDemoMode) return
    setLoading(true)
    // Pull all card_products across all cards for this site (usually just the Standard one).
    supabase
      .from('cards')
      .select('id, name, card_products(id, overrides, default_qty, sort_order, products(id, sku, name, description, unit, pack_size, price, image_url))')
      .eq('company_id', activeCompanyId)
      .is('archived_at', null)
      .then(({ data }) => {
        // Flatten + dedupe by product_id (a product on multiple cards still shows once).
        const seen = new Map()
        for (const card of data || []) {
          for (const cp of card.card_products || []) {
            const pid = cp.products?.id
            if (!pid || seen.has(pid)) continue
            seen.set(pid, cp)
          }
        }
        setProducts(Array.from(seen.values()))
        setLoading(false)
      })
  }, [activeCompanyId])

  if (!activeCompanyId) {
    return (
      <div className="card max-w-2xl">
        <h3 className="font-semibold text-gray-800 mb-2">Pick a specific site to see its catalogue</h3>
        <p className="text-sm text-gray-600">
          Available products can differ from site to site. Use the site picker in the sidebar to
          choose which site's catalogue you'd like to browse — the rest of the app (My Orders,
          Reports, Activity) already shows every site combined.
        </p>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="card">
        <h3 className="font-semibold text-gray-800 mb-1">Products available to this site</h3>
        <p className="text-sm text-gray-500">
          Browse what's available to order. To place an order, head to <strong>Place Order</strong>.
        </p>
      </div>

      {loading && <p className="text-sm text-gray-500">Loading…</p>}

      {!loading && products.length === 0 && (
        <div className="card text-sm text-gray-500">
          No products available to this site yet. Your account manager can add them on the back end.
        </div>
      )}

      {products.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {products.map(cp => {
            const p = cp.products || {}
            const name = effective(cp, 'name')
            const unit = effective(cp, 'unit')
            const pack = effective(cp, 'pack_size')
            const price = effective(cp, 'price')
            return (
              <button
                key={cp.id}
                onClick={() => setOpen(cp)}
                className="text-left rounded-xl border border-gray-200 bg-white hover:border-brand hover:shadow-md transition-all overflow-hidden flex flex-col"
              >
                <div className="aspect-square bg-brand-50 flex items-center justify-center overflow-hidden p-3">
                  {p.image_url
                    ? <img src={p.image_url} alt="" className="max-w-full max-h-full object-contain" />
                    : <Package className="w-16 h-16 text-brand opacity-30" />
                  }
                </div>
                <div className="p-4 flex-1 flex flex-col">
                  <div className="font-medium text-gray-800 truncate">{name}</div>
                  <div className="text-xs text-gray-500 mt-1">
                    {p.sku}
                    {unit ? ` · ${unit}` : ''}
                    {pack ? ` · ${pack}/box` : ''}
                  </div>
                  {price != null && (
                    <div className="mt-2 text-lg font-semibold text-brand-dark">£{Number(price).toFixed(2)}</div>
                  )}
                </div>
              </button>
            )
          })}
        </div>
      )}

      {open && (() => {
        const p = open.products || {}
        const name = effective(open, 'name')
        const desc = effective(open, 'description')
        const unit = effective(open, 'unit')
        const pack = effective(open, 'pack_size')
        const price = effective(open, 'price')
        return (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setOpen(null)}>
            <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-auto" onClick={e => e.stopPropagation()}>
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
                <h3 className="font-semibold text-gray-800">{name}</h3>
                <button onClick={() => setOpen(null)} className="text-gray-400 hover:text-gray-700">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="p-6 space-y-4">
                {p.image_url && (
                  <div className="bg-gray-50 rounded-lg p-4 flex items-center justify-center">
                    <img src={p.image_url} alt="" className="max-w-full max-h-80 object-contain" />
                  </div>
                )}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                  <div>
                    <div className="text-xs text-gray-500 uppercase tracking-wide">SKU</div>
                    <div className="font-medium text-gray-800">{p.sku}</div>
                  </div>
                  {unit && (
                    <div>
                      <div className="text-xs text-gray-500 uppercase tracking-wide">Unit</div>
                      <div className="font-medium text-gray-800">{unit}</div>
                    </div>
                  )}
                  {pack && (
                    <div>
                      <div className="text-xs text-gray-500 uppercase tracking-wide">Pack size</div>
                      <div className="font-medium text-gray-800">{pack}/box</div>
                    </div>
                  )}
                  {price != null && (
                    <div>
                      <div className="text-xs text-gray-500 uppercase tracking-wide">Price</div>
                      <div className="font-medium text-gray-800">£{Number(price).toFixed(2)}</div>
                    </div>
                  )}
                </div>
                {desc && (
                  <div>
                    <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">Description</div>
                    <p className="text-sm text-gray-700 whitespace-pre-wrap">{desc}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )
      })()}
    </div>
  )
}
